import type { Decision } from '@kkp-fraud/shared';

/**
 * Investigate's contract with the admin service (`dotnet/admin-api`, `CaseEndpoints`) and its
 * pure helpers. The BFF owns the workflow: every case it returns carries `allowedTransitions`,
 * and the screen shows exactly those buttons — it keeps no copy of the transition table.
 *
 * These routes are the BFF's own surface (cookie mode only), like `/api/masters`; the engine
 * knows nothing about cases, so they are not in `packages/shared`.
 */

export const CASE_ROUTES = {
  cases: '/api/cases',
  precision: '/api/cases/rule-precision',
  decisions: '/api/investigate/decisions',
} as const;

export const CASE_STATUSES = ['Open', 'UnderReview', 'ConfirmedFraud', 'FalsePositive', 'Genuine', 'Closed'] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

/** The three labels — what precision counts and what a case's `resolution` can be. */
export const CASE_LABELS = ['ConfirmedFraud', 'FalsePositive', 'Genuine'] as const;
export type CaseLabel = (typeof CASE_LABELS)[number];

export const CASE_PRIORITIES = ['low', 'medium', 'high'] as const;
export type CasePriority = (typeof CASE_PRIORITIES)[number];

export interface CaseRule {
  ruleId: string;
  name?: string;
  action?: string;
  detail?: string;
  branch?: number;
  /** 2c rule sets — the hit's set, present only when the decision evaluated two or more sets. */
  ruleSetId?: string;
}

/** The transaction fields a case keeps from its decision (the decision itself expires after 90 days). */
export interface CaseTxn {
  transactionId?: string;
  cardId: string;
  customerId?: string;
  merchantId?: string;
  merchantName?: string;
  merchantCity?: string;
  terminalId?: string;
  amount?: number;
  amountThb?: number;
  currencyCodeNum?: number;
  channelId?: string;
  messageType?: string;
  txnType?: string;
  mcc?: number;
  merchantCountryNum?: number;
  acquirerCountryNum?: number;
  entryMode?: string;
  cardPresent?: boolean;
  ecommerce?: boolean;
  threeDsAuthenticated?: boolean;
  isAtm?: boolean;
  network?: string;
  product?: string;
  fraudScore?: number;
  localHour?: number;
}

/** One decision as Investigate shows it — the same snapshot a case keeps. */
export interface DecisionSnapshot {
  decisionId: string;
  cardId: string;
  customerId?: string;
  merchantId?: string;
  decisionUtc: string;
  decision: Decision;
  ruleSetVersion?: string;
  engineVersion?: string;
  degraded: boolean;
  engineLatencyMs?: number;
  serviceLatencyMs?: number;
  firedRules: CaseRule[];
  skippedRules: string[];
  stateIncomplete: string[];
  reasons: string[];
  txn: CaseTxn;
}

/** A row of the card's timeline: the decision, and its case when it has one. */
export interface InvestigateDecision extends DecisionSnapshot {
  caseId?: string;
}

export interface InvestigatePage {
  cardId: string;
  /** `engine` (the decision API's feed, scanned) or `cosmos` (the card's partition). */
  source: string;
  items: InvestigateDecision[];
  nextCursor?: string;
  /** The engine scan stopped at `scanLimit` rows: older decisions of the card may exist. */
  capped?: boolean;
  scanLimit?: number;
}

export interface CaseNote {
  id: string;
  author: string;
  utc: string;
  text: string;
  editedUtc?: string;
}

export interface CaseHistoryEntry {
  utc: string;
  actor: string;
  from?: CaseStatus;
  to: CaseStatus;
  note?: string;
}

export interface Case extends Omit<DecisionSnapshot, 'decisionId'> {
  id: string;
  decisionId: string;
  status: CaseStatus;
  resolution?: CaseLabel;
  assignee?: string;
  priority?: CasePriority;
  notes: CaseNote[];
  history: CaseHistoryEntry[];
  createdUtc: string;
  createdBy: string;
  updatedUtc: string;
  updatedBy: string;
  _etag: string;
  allowedTransitions: CaseStatus[];
  noteEditMinutes: number;
}

export interface CasePage {
  items: Case[];
  nextCursor?: string;
}

export interface CaseFilters {
  status?: CaseStatus;
  resolution?: CaseLabel;
  ruleId?: string;
  merchantId?: string;
  customerId?: string;
  cardId?: string;
  assignee?: string;
}

export interface RulePrecisionRow {
  ruleId: string;
  name?: string;
  confirmedFraud: number;
  falsePositive: number;
  genuine: number;
  open: number;
  labelled: number;
  /** `null` — not 0 — while no case of the rule is labelled. */
  precision: number | null;
}

export interface RulePrecisionReport {
  cases: number;
  rules: RulePrecisionRow[];
  from?: string;
  to?: string;
}

/** The chip colour of each status (the classes `Chips.tsx` uses for actions). */
export const STATUS_CLASS: Record<CaseStatus, string> = {
  Open: 'info',
  UnderReview: 'warn',
  ConfirmedFraud: 'bad',
  FalsePositive: 'mute',
  Genuine: 'ok',
  Closed: 'mute',
};

/** The case list's text filters, in the order the form shows them. */
export const CASE_TEXT_FILTERS = ['ruleId', 'merchantId', 'customerId', 'cardId', 'assignee'] as const;
export type CaseTextFilter = (typeof CASE_TEXT_FILTERS)[number];

/**
 * The longest value the BFF takes for each text filter (`CaseEndpoints.MaxIdLength` 128,
 * `MaxAssigneeLength` 200) — the card search of the timeline takes the same 128. The screen
 * checks before it asks, so an over-long value gets a sentence, not the server's 400 (QA-I-05).
 */
export const CASE_FILTER_MAX: Record<CaseTextFilter, number> = { ruleId: 128, merchantId: 128, customerId: 128, cardId: 128, assignee: 200 };

/** The text filters whose trimmed value is longer than the BFF takes. */
export function overlongFilters(filters: CaseFilters): CaseTextFilter[] {
  return CASE_TEXT_FILTERS.filter((k) => (filters[k]?.trim().length ?? 0) > CASE_FILTER_MAX[k]);
}

const nextDay = (ymd: string): string | undefined => {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== ymd) return undefined;
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/**
 * Two picked days, both inclusive, as the instants the BFF's `from` / `to` mean (from
 * inclusive, to exclusive): Bangkok midnight of the first day, Bangkok midnight after the last.
 * Every date on the console is a Thai date, and a bare `YYYY-MM-DD` would be read by the BFF as
 * UTC midnight — a decision made 00:00–06:59 Bangkok counted on the day before (QA-I-01).
 * Thailand has no daylight saving, so midnight is always `+07:00`. Either end may be empty (open).
 */
export function bangkokDayRange(from: string, to: string): { from?: string; to?: string } {
  const valid = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s) && nextDay(s) !== undefined;
  const end = valid(to) ? nextDay(to) : undefined;
  return {
    ...(valid(from) ? { from: `${from}T00:00:00+07:00` } : {}),
    ...(end ? { to: `${end}T00:00:00+07:00` } : {}),
  };
}

/** `?status=…&ruleId=…&cursor=…` — empty filters are left out. */
export function caseQuery(filters: CaseFilters, cursor?: string, limit?: number): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    const s = typeof v === 'string' ? v.trim() : '';
    if (s) qs.set(k, s);
  }
  if (limit !== undefined) qs.set('limit', String(limit));
  if (cursor) qs.set('cursor', cursor);
  const tail = qs.toString();
  return tail ? `?${tail}` : '';
}

/** One case's route: the card is the store's partition key, so it is part of the address. */
export function casePath(cardId: string, id: string, suffix = ''): string {
  return `${CASE_ROUTES.cases}/${encodeURIComponent(cardId)}/${encodeURIComponent(id)}${suffix}`;
}

/** The console's own deep link to a case. */
export function caseLink(cardId: string, id: string): string {
  return `/investigate/${encodeURIComponent(cardId)}/${encodeURIComponent(id)}`;
}

/** May `actor` still change this note? The author, within the case's edit window. The BFF decides; this only hides buttons. */
export function noteEditable(note: CaseNote, actor: string | undefined, minutes: number, now = Date.now()): boolean {
  if (!actor || note.author !== actor) return false;
  const written = Date.parse(note.utc);
  return Number.isFinite(written) && now - written <= minutes * 60_000;
}

/** `66.7%`, or `—` while nothing is labelled. */
export function precisionText(p: number | null | undefined): string {
  return p === null || p === undefined || !Number.isFinite(p) ? '—' : `${(p * 100).toFixed(1)}%`;
}

export type PrecisionSortKey = 'ruleId' | 'precision' | 'labelled' | 'confirmedFraud' | 'falsePositive' | 'genuine' | 'open';

/**
 * Sorted by one column. Rules with no labelled case (`precision: null`) always sink to the
 * bottom of a precision sort, whichever the direction: "no verdict yet" is not "0%".
 */
export function sortPrecision(rows: readonly RulePrecisionRow[], key: PrecisionSortKey, dir: 'asc' | 'desc'): RulePrecisionRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (key === 'ruleId') return sign * a.ruleId.localeCompare(b.ruleId);
    if (key === 'precision') {
      if (a.precision === null && b.precision === null) return a.ruleId.localeCompare(b.ruleId);
      if (a.precision === null) return 1;
      if (b.precision === null) return -1;
      return sign * (a.precision - b.precision) || b.labelled - a.labelled || a.ruleId.localeCompare(b.ruleId);
    }
    return sign * (a[key] - b[key]) || a.ruleId.localeCompare(b.ruleId);
  });
}

/**
 * The same arithmetic as the BFF's `RulePrecision.Compute`, for the demo layer only (the real
 * screen shows what the BFF computed): a case counts for every rule in its `firedRules`, by its
 * label while labelled or closed, as open while Open or UnderReview, and not at all when it was
 * closed without an outcome.
 */
export function rulePrecision(cases: readonly Pick<Case, 'status' | 'resolution' | 'firedRules'>[]): RulePrecisionRow[] {
  const acc = new Map<string, RulePrecisionRow>();
  for (const c of cases) {
    // Closed without an outcome (straight from Open) counts nowhere — not even as open.
    if (c.status === 'Closed' && !c.resolution) continue;
    const bucket: CaseLabel | 'open' =
      c.status === 'ConfirmedFraud' || c.status === 'FalsePositive' || c.status === 'Genuine'
        ? c.status
        : c.status === 'Closed' && c.resolution
          ? c.resolution
          : 'open';
    for (const rule of new Map(c.firedRules.map((r) => [r.ruleId, r])).values()) {
      const row = acc.get(rule.ruleId) ?? {
        ruleId: rule.ruleId,
        ...(rule.name ? { name: rule.name } : {}),
        confirmedFraud: 0,
        falsePositive: 0,
        genuine: 0,
        open: 0,
        labelled: 0,
        precision: null,
      };
      if (bucket === 'ConfirmedFraud') row.confirmedFraud++;
      else if (bucket === 'FalsePositive') row.falsePositive++;
      else if (bucket === 'Genuine') row.genuine++;
      else row.open++;
      row.labelled = row.confirmedFraud + row.falsePositive + row.genuine;
      row.precision = row.labelled === 0 ? null : row.confirmedFraud / row.labelled;
      acc.set(rule.ruleId, row);
    }
  }
  return [...acc.values()].sort((a, b) => (a.ruleId < b.ruleId ? -1 : a.ruleId > b.ruleId ? 1 : 0));
}
